import React from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";

import {
  contentReviewService,
  type ContentReviewItem,
  type ContentReviewScene,
} from "../../services/contentReviewService";

import {selectableBeats} from '../../lib/shotCopyTarget';

/**
 * PKT-0601 · P1 — THE REEL, INSIDE THE SHEET THAT ASKS ABOUT IT.
 *
 * Grant: "if you click review the video should pop up too there to be able to
 * watch & review."
 *
 * Before this, the review sheet named the reel's SUBJECT and showed nothing
 * else. The player was on the card behind it, under the sheet's own backdrop —
 * so the one moment the reviewer is asked "what should change?" was the one
 * moment he could not see the thing being changed. He had to cancel, watch,
 * re-open, and hold it in his head while typing.
 *
 * 🔴 WHY THIS IS A SEPARATE COMPONENT AND NOT A PROP ON THE CARD.
 * ContentReviewCard's player is built for a GRID: preload="none", hover-intent
 * before the first byte, mute-by-default, play/pause on hover, no controls. All
 * of that is correct for fifteen cards you are scanning past and wrong for one
 * reel you deliberately opened to study. This player is the opposite on every
 * one of those axes — it loads on open, shows real transport controls, and
 * seeks. Sharing one component would have meant a flag on each behaviour, and
 * the card's version is pinned by tests that PKT-0601 must not disturb.
 *
 * ⚠️ KNOWN DUPLICATION, DELIBERATE, AND NAMED SO IT IS NOT REDISCOVERED AS A
 * BUG: the signed-URL fetch here is a second, simpler implementation of the one
 * at ContentReviewCard.tsx:583-602. Unifying them means editing the card, whose
 * refusal-display pins (#457/#458) this packet is fenced away from. The honest
 * trade is a small duplicate now over a risky edit to a 1,500-line file whose
 * tests are the estate's only regression net on the refusal path. Unify it in
 * the packet that next touches the card's player for its own reasons.
 */

interface Props {
  /**
   * The reel the sheet is about, and the ONLY input this needs.
   *
   * 🔴 THERE IS DELIBERATELY NO `open` PROP. The first draft had one, and a
   * mutation test killed it: forcing `open={true}` changed nothing, because
   * `sendBackTarget` is already null exactly when the sheet is shut — the
   * Drawer derives its own `open` from the very same value. A second flag that
   * must always agree with the first is not a safety net, it is a way for them
   * to disagree. One source of truth: an item means the sheet is open.
   */
  item: ContentReviewItem | null;
  selectedSceneId?: string | null;
  onSelectScene?: (scene: ContentReviewScene) => void;
  disabled?: boolean;
  seekTo?: {offsetSec: number; sequence: number};
}

const FRAME = {
  width: "100%",
  /*
   * Reels are 9:16. Unbounded, one would push the dials and the note field off
   * a phone screen entirely — the sheet is anchored to the bottom, so anything
   * tall enough steals the very controls it exists to support. Capping by
   * VIEWPORT height rather than pixels keeps the note reachable on a short
   * phone in landscape as well as a desktop window.
   */
  maxHeight: "40vh",
  borderRadius: 2,
  bgcolor: "common.black",
  display: "block",
  objectFit: "contain",
} as const;

const ReviewSheetVideo: React.FC<Props> = ({ item, selectedSceneId, onSelectScene, disabled = false, seekTo }) => {
  const playerRef = React.useRef<HTMLVideoElement | null>(null);
  const pendingSeek = React.useRef<number | null>(null);
  const [url, setUrl] = React.useState<string | null>(null);
  const [failed, setFailed] = React.useState(false);

  const itemId = item?.id ?? null;
  const hasVideo = Boolean(item?.video_available);

  React.useEffect(() => {
    /*
     * A closed sheet fetches nothing. A signed URL costs egress and lives ~60s;
     * minting one for a sheet nobody opened is pure waste — the same reason the
     * card delays its first byte behind hover intent.
     *
     * `!itemId` is here for TYPE NARROWING, not for behaviour: `hasVideo` is
     * already false for a null item, so the runtime property holds without it.
     * Stated because a mutation proved no test can distinguish the two, and an
     * unkillable clause with a confident comment is how dead code survives.
     */
    if (!itemId || !hasVideo) {
      setUrl(null);
      setFailed(false);
      return;
    }
    /*
     * `stale` is what keeps the WRONG REEL off the screen. Open card A, close,
     * open card B: A's request can still land after B's. Without this guard the
     * later-resolving older fetch wins and the sheet shows A's video while
     * every label around it says B — the exact class of defect the sheet-scoped
     * test asserts against by ID rather than by index.
     */
    let stale = false;
    pendingSeek.current = null;
    setUrl(null);
    setFailed(false);
    void contentReviewService
      .media(itemId, "video")
      .then((link) => {
        if (stale) return;
        setUrl(link.url);
      })
      .catch(() => {
        if (stale) return;
        setFailed(true);
      });
    return () => {
      stale = true;
    };
  }, [itemId, hasVideo, item?.fingerprint]);

  React.useEffect(() => {
    if (!seekTo || !Number.isFinite(seekTo.offsetSec) || seekTo.offsetSec < 0) return;
    pendingSeek.current = seekTo.offsetSec;
    const player = playerRef.current;
    if (player) {
      player.pause();
      if (player.readyState >= 1) {player.currentTime = seekTo.offsetSec; pendingSeek.current = null;}
    }
  }, [seekTo]);

  if (!item) return null;

  if (!hasVideo || failed) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
        data-testid="review-sheet-video-unavailable"
        sx={{ mb: 2 }}
      >
        {/*
          SAY WHICH IT IS. "No video" and "the video would not load" send the
          reviewer to two different places — one is this reel's render, the
          other is this session — and a single vague line would waste his time
          on whichever he guessed wrong. The note stays usable either way: a
          missing player must never cost a review that was already typed.
        */}
        {hasVideo
          ? "The video couldn’t be loaded just now. Your note still files normally."
          : "No video was rendered for this reel yet. Your note still files normally."}
      </Typography>
    );
  }

  if (!url) {
    return (
      <Box
        data-testid="review-sheet-video-loading"
        sx={{ display: "flex", justifyContent: "center", py: 3, mb: 2 }}
      >
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <>
    <Box
      ref={playerRef}
      component="video"
      data-testid="review-sheet-video"
      src={url}
      controls
      onLoadedMetadata={() => {
        if (playerRef.current && pendingSeek.current !== null) {
          playerRef.current.currentTime = pendingSeek.current;
          pendingSeek.current = null;
        }
      }}
      playsInline
      preload="metadata"
      aria-label={`${item.subject} reel`}
      sx={{ ...FRAME, mb: 2 }}
    />
    {onSelectScene && selectableBeats(item.scenes).length > 0 && (
      <Box role="group" aria-label="Select a beat to edit its words" sx={{mb: 2}}>
        <Typography variant="body2">Select a beat to edit its on-screen words.</Typography>
        {selectableBeats(item.scenes).map(scene => (
          <Button key={scene.id} disabled={disabled} aria-pressed={selectedSceneId === scene.id}
            variant={selectedSceneId === scene.id ? 'contained' : 'outlined'}
            sx={{m: 0.5, minHeight: 44}}
            onClick={() => {
              const player = playerRef.current;
              pendingSeek.current = scene.offset_sec!;
              if (player) {
                player.pause();
                if (player.readyState >= 1) { player.currentTime = scene.offset_sec!; pendingSeek.current = null; }
              }
              onSelectScene(scene);
            }}>
            {scene.label || scene.id} · {scene.offset_sec!.toFixed(1)}–{(scene.offset_sec! + scene.duration_sec!).toFixed(1)}s
          </Button>
        ))}
      </Box>
    )}
    </>
  );
};

export default ReviewSheetVideo;
